import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { AiAdminApiService, AiUsageStats, AiTopUser } from '../../services/ai-admin-api.service';
import { StorageService } from '../../../../services/storage.service';

type PeriodType = 'month' | 'all-time';

@Component({
    selector: 'app-ai-dashboard',
    templateUrl: './dashboard-page.component.html',
    styleUrls: ['./dashboard-page.component.scss']
})
export class AiDashboardPageComponent implements OnInit, OnDestroy {
    loading = true;
    error: string | null = null;
    stats: AiUsageStats | null = null;
    selectedPeriod: PeriodType = 'month';

    private destroy$ = new Subject<void>();
    private companyId: string;

    constructor(
        private aiAdminApi: AiAdminApiService,
        private storageService: StorageService,
    ) { }

    ngOnInit(): void {
        this.companyId = this.storageService.get('company')?.id || '';
        if (!this.companyId) {
            this.error = 'No se pudo obtener la información de la empresa';
            this.loading = false;
            return;
        }
        this.loadStats();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private loadStats(): void {
        this.loading = true;
        this.error = null;
        this.aiAdminApi.getUsageStats(this.companyId)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (stats) => {
                    this.stats = stats;
                    this.loading = false;
                },
                error: (err) => {
                    this.loading = false;
                    this.error = this._getErrorMessage(err);
                }
            });
    }

    changePeriod(period: any): void {
        this.selectedPeriod = period as PeriodType;
    }

    private _getErrorMessage(err: any): string {
        if (err.status === 401) {
            return 'No tienes permiso para acceder a esta información';
        }
        if (err.status === 403) {
            return 'Solo administradores pueden ver estas estadísticas';
        }
        if (err.status === 404) {
            return 'No se encontró la información de uso';
        }
        if (err.error?.message) {
            return err.error.message;
        }
        return 'Error al cargar las estadísticas. Por favor, intenta de nuevo';
    }

    /**
     * Retorna el valor a mostrar según el período seleccionado
     */
    getTokensValue(): number {
        if (!this.stats) return 0;
        switch (this.selectedPeriod) {
            case 'month':
                return this.stats.totalTokensThisMonth;
            case 'all-time':
                return this.stats.totalTokensAllTime;
            default:
                return 0;
        }
    }

    /**
     * Calcula el máximo de tokens en los últimos 30 días para escalar el gráfico
     */
    getMaxTokensByDay(): number {
        if (!this.stats?.tokensByDay || this.stats.tokensByDay.length === 0) {
            return 10000;
        }
        return Math.max(...this.stats.tokensByDay.map(d => d.tokens));
    }

    /**
     * Calcula el ancho de la barra como porcentaje
     */
    getBarWidth(tokens: number): string {
        const maxTokens = this.getMaxTokensByDay();
        const percentage = (tokens / maxTokens) * 100;
        return `${Math.min(percentage, 100)}%`;
    }

    /**
     * Obtiene el nombre del usuario, usando el email si el nombre es null
     */
    getUserDisplayName(user: AiTopUser): string {
        if (user.userName) {
            return user.userName;
        }
        // Extraer nombre del email (parte antes del @) y formatear
        const emailPart = user.email.split('@')[0];
        return emailPart
            .split('.')
            .map(part => part.charAt(0).toUpperCase() + part.slice(1))
            .join(' ');
    }
}
